import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import { LocationOn, Today, Person } from '@mui/icons-material';

const defaultImage = 'https://via.placeholder.com/300x200?text=No+Image+Available';

function CardinalCard({ cardinal, index }) {
  const navigate = useNavigate();
  
  // Handle missing data
  const {
    name = 'Unknown Name',
    country = 'Unknown Country',
    birth_date = 'Unknown Birth Date',
    appointing_pope = 'Unknown Pope',
    photo_url,
    biography_text = ''
  } = cardinal;

  const handleViewDetails = () => {
    // Use the index if available, otherwise use the name as a fallback
    if (typeof index === 'number') {
      navigate(`/cardinals/${index}`);
    } else {
      // Create a simple slug - for more reliable navigation
      const simpleName = name.split(' ').pop().toLowerCase();
      navigate(`/cardinals/${simpleName}`);
    }
  };

  // Get a short bio excerpt if available
  const bioExcerpt = biography_text 
    ? biography_text.substring(0, 120) + (biography_text.length > 120 ? '...' : '')
    : 'No biography available.';

  return (
    <Card 
      className="cardinal-card"
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        overflow: 'visible',
        bgcolor: 'transparent',
        boxShadow: 'none',
        '&:hover': {
          transform: 'translateY(-5px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          bottom: 8,
          border: '1px solid rgba(139, 69, 19, 0.4)',
          borderRadius: 2,
          zIndex: 0
        }
      }}
    >
      {/* Ornamental corners - using CSS instead of images */}
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '20px', 
          height: '20px', 
          borderTop: '2px solid rgba(139, 69, 19, 0.7)',
          borderLeft: '2px solid rgba(139, 69, 19, 0.7)',
          borderRadius: '2px 0 0 0',
          zIndex: 1,
          transform: 'translate(-5px, -5px)'
        }} 
      />
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 0, 
          right: 0, 
          width: '20px', 
          height: '20px', 
          borderTop: '2px solid rgba(139, 69, 19, 0.7)',
          borderRight: '2px solid rgba(139, 69, 19, 0.7)',
          borderRadius: '0 2px 0 0',
          zIndex: 1,
          transform: 'translate(5px, -5px)'
        }} 
      />
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          width: '20px', 
          height: '20px', 
          borderBottom: '2px solid rgba(139, 69, 19, 0.7)',
          borderLeft: '2px solid rgba(139, 69, 19, 0.7)',
          borderRadius: '0 0 0 2px',
          zIndex: 1,
          transform: 'translate(-5px, 5px)'
        }} 
      />
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 0, 
          right: 0, 
          width: '20px', 
          height: '20px', 
          borderBottom: '2px solid rgba(139, 69, 19, 0.7)',
          borderRight: '2px solid rgba(139, 69, 19, 0.7)',
          borderRadius: '0 0 2px 0',
          zIndex: 1,
          transform: 'translate(5px, 5px)'
        }} 
      />
      
      {/* Main content with parchment background */}
      <Paper 
        elevation={3}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          backgroundColor: '#f9f2e3', // Parchment color
          backgroundImage: 'repeating-linear-gradient(to right, rgba(139, 69, 19, 0.02), rgba(139, 69, 19, 0.03) 1px, #f9f2e3 1px, #f9f2e3 20px)',
          backgroundBlendMode: 'multiply',
          border: '1px solid rgba(139, 69, 19, 0.6)',
          borderRadius: 2,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/png;base64,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") repeat',
            opacity: 0.03,
            zIndex: 0
          }
        }}
      >
        {/* Cardinal Photo with decorative frame */}
        <Box sx={{ position: 'relative', pt: 3, px: 3, pb: 1 }}>
          <Box 
            sx={{ 
              position: 'relative',
              height: '250px',
              borderRadius: 1,
              border: '1px solid rgba(139, 69, 19, 0.6)',
              boxShadow: 'inset 0 0 8px rgba(139, 69, 19, 0.3)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 5,
                left: 5,
                right: 5,
                bottom: 5,
                border: '1px solid rgba(139, 69, 19, 0.3)',
                zIndex: 1,
                pointerEvents: 'none'
              }
            }}
          >
            <CardMedia
              component="img"
              height="250"
              image={photo_url || defaultImage}
              alt={name}
              sx={{ 
                objectFit: 'contain', 
                bgcolor: '#f5f5f5',
                objectPosition: 'center'
              }}
            />
          </Box>
        </Box>
        
        {/* Cardinal Content */}
        <CardContent 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            px: 3,
            pb: 3
          }}
        >
          {/* Decorative title separator */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                borderBottom: '1px solid rgba(139, 69, 19, 0.4)',
                mx: 1
              }
            }}
          >
            <Typography 
              variant="h5" 
              component="h2" 
              align="center"
              sx={{ 
                color: '#5D1D0D', // Deep red color for titles
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {name}
            </Typography>
          </Box>
          
          <Box 
            sx={{ 
              mb: 2, 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: 1 
            }}
          >
            <Chip 
              icon={<LocationOn sx={{ color: '#5D1D0D !important' }} />} 
              label={country} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(139, 69, 19, 0.1)', 
                border: '1px solid rgba(139, 69, 19, 0.3)',
                color: '#5D1D0D',
                '& .MuiChip-icon': {
                  color: '#5D1D0D'
                }
              }} 
            />
            <Chip 
              icon={<Today sx={{ color: '#5D1D0D !important' }} />} 
              label={birth_date} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(139, 69, 19, 0.1)', 
                border: '1px solid rgba(139, 69, 19, 0.3)',
                color: '#5D1D0D',
                '& .MuiChip-icon': {
                  color: '#5D1D0D'
                }
              }} 
            />
            <Chip 
              icon={<Person sx={{ color: '#5D1D0D !important' }} />} 
              label={`Appointed by ${appointing_pope}`} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(139, 69, 19, 0.1)', 
                border: '1px solid rgba(139, 69, 19, 0.3)',
                color: '#5D1D0D',
                '& .MuiChip-icon': {
                  color: '#5D1D0D'
                }
              }} 
            />
          </Box>
          
          {/* Bio text with dropcap style */}
          <Typography 
            variant="body2" 
            paragraph
            sx={{ 
              color: '#333',
              textAlign: 'justify',
              '&::first-letter': {
                fontSize: '1.8em',
                color: '#5D1D0D',
                fontWeight: 'bold',
                float: 'left',
                lineHeight: 0.9,
                marginRight: '0.1em'
              }
            }}
          >
            {bioExcerpt}
          </Typography>
          
          <Box sx={{ mt: 'auto' }}>
            {/* Decorative divider */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                my: 2,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  height: '1px',
                  background: 'linear-gradient(to right, transparent, rgba(139, 69, 19, 0.4), transparent)',
                }
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  mx: 2, 
                  color: 'rgba(139, 69, 19, 0.6)', 
                  fontSize: '1.5rem',
                  lineHeight: 0
                }}
              >
                ✠
              </Box>
            </Box>
            
            <Button 
              variant="outlined" 
              onClick={handleViewDetails}
              fullWidth
              sx={{
                color: '#5D1D0D',
                borderColor: 'rgba(139, 69, 19, 0.6)',
                backgroundColor: 'rgba(139, 69, 19, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  borderColor: 'rgba(139, 69, 19, 0.8)',
                }
              }}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </Paper>
    </Card>
  );
}

export default CardinalCard; 